'use strict';

/**
 * The attached-prompts panel: mounted under a Work Item detail or a Test Run,
 * it lists the prompts attached to that entity (each frozen at the version it
 * was attached as), lets content editors attach one from the libraries, paste
 * and attach an external prompt in one move, record a run in place (a Test
 * Run's panel ties the run to the Test Run), and detach.
 *
 * This is the visible end of the refinement cycle: attach a prompt to the
 * work, run it, capture the outcome, refine in the library, re-attach.
 *
 * One instance serves every host (only one detail page is visible at a time);
 * hosts call showFor(ownerType, idOwner, containerID) from onAfterRender.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libFocus = require('../FocusFilters.js');
const libDescriptors = require('../EntityDescriptors.js');
const libPromptEmbed = require('../PromptEmbed.js');

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _shortDate(pDate)
{
	if (!pDate) { return ''; }
	let tmpStamp = Date.parse(pDate);
	if (isNaN(tmpStamp)) { return String(pDate); }
	return new Date(tmpStamp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const _ViewConfiguration =
{
	ViewIdentifier: 'Plansheet-PromptAttachments',
	DefaultRenderable: 'Plansheet-PromptAttachments',
	DefaultDestinationAddress: '#Entity-Prompts',
	AutoRender: false,

	CSS: /*css*/`
		.ppa-row { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; background: rgba(8,14,24,0.34); border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 8px; padding: 9px 12px; margin-bottom: 8px; }
		.ppa-title { font-weight: 600; font-size: 13.5px; color: var(--cnx-txt, #eef2f7); }
		.ppa-meta { font-size: 11.5px; color: var(--cnx-lbl, #9aabc0); }
		.ppa-badge { font-family: ui-monospace, Menlo, monospace; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; padding: 1px 7px; border-radius: 6px; border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); color: var(--cnx-lbl, #9aabc0); white-space: nowrap; }
		.ppa-btn { border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); background: rgba(8,14,24,0.4); color: var(--cnx-txt, #eef2f7); border-radius: 7px; padding: 3px 11px; font-size: 12px; cursor: pointer; white-space: nowrap; }
		.ppa-btn:hover { border-color: #f0c44a; color: #f0c44a; }
		.ppa-actions { display: flex; gap: 8px; margin-top: 10px; }
		.ppa-empty { color: var(--cnx-lbl, #9aabc0); font-size: 12.5px; padding: 4px 2px; }
		.ppa-spacer { flex: 1; }
		.ppa-run { display: flex; align-items: center; gap: 9px; margin: -3px 0 10px 0; padding: 6px 12px; background: rgba(8,14,24,0.22); border: 1px dashed var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 8px; }
		.ppa-run-text { flex: 1; font-family: ui-monospace, Menlo, monospace; font-size: 11.5px; color: var(--cnx-txt, #eef2f7); white-space: pre-wrap; word-break: break-word; }
		/* The "bundle other work items" picker in the Generate dialog (#236): a filterable,
		   multi-select mini list view in place of the old flat, database-ordered dropdown. It sits
		   inside a standard modal, so it is themed with the app --theme-color-* vars (light + dark). */
		.ppa-pick-search { width: 100%; margin-bottom: 8px; }
		.ppa-pick-chiprow { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }
		.ppa-pick-chip { border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 2px 11px; font-size: 11.5px; cursor: pointer; }
		.ppa-pick-chip:hover { background: var(--theme-color-background-hover, #eaf2ff); }
		.ppa-pick-chip.on { background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff); border-color: transparent; }
		.ppa-pick-scroll { max-height: 42vh; overflow: auto; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; }
		.ppa-pick-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
		.ppa-pick-table th { position: sticky; top: 0; z-index: 1; background: var(--theme-color-background-panel, #fff); text-align: left; padding: 6px 10px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); border-bottom: 1px solid var(--theme-color-border-default, #e6e9ee); }
		.ppa-pick-table td { padding: 6px 10px; border-bottom: 1px solid var(--theme-color-border-default, #eef1f6); color: var(--theme-color-text-primary, #2c3140); white-space: nowrap; }
		.ppa-pick-table tr:last-child td { border-bottom: none; }
		.ppa-pick-row { cursor: pointer; }
		.ppa-pick-row:hover td { background: var(--theme-color-background-hover, #eaf2ff); }
		.ppa-pick-row.on td { background: var(--theme-color-background-secondary, #eef1f6); }
		.ppa-pick-title { white-space: normal; min-width: 15rem; }
		.ppa-pick-type { text-align: center; }
		.ppa-pick-type svg { width: 16px; height: 16px; vertical-align: middle; color: var(--theme-color-text-secondary, #5b6376); }
		.ppa-pick-num, .ppa-pick-status, .ppa-pick-upd { color: var(--theme-color-text-secondary, #5b6376); }
		.ppa-pick-cb { width: 1px; }
		.ppa-pick-empty { text-align: center; color: var(--theme-color-text-secondary, #5b6376); padding: 16px; }
		.ppa-pick-count { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); padding: 6px 4px 0; }
		.ppa-pick-selected { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
	`,

	Templates: [{ Hash: 'Plansheet-PromptAttachments', Template: /*html*/`<div></div>` }],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-PromptAttachments', TemplateHash: 'Plansheet-PromptAttachments', ContentDestinationAddress: '#Entity-Prompts', RenderMethod: 'replace' }
	]
};

class PictViewPromptAttachments extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._ownerType = null;
		this._idOwner = 0;
		this._ownerNumber = 0;
		this._containerID = null;
		this._links = [];
		this._embeds = [];
		// When set, _paint mounts that prompt's embed expanded (so Generate ends with it open, no modal).
		this._expandIDPrompt = 0;
	}

	_api() { return this.pict.providers.PromptAPI; }
	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_canEdit()
	{
		let tmpApp = this.pict.PictApplication;
		return !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
	}

	showFor(pOwnerType, pIDOwner, pContainerID, pOwnerNumber)
	{
		this._ownerType = pOwnerType;
		this._idOwner = pIDOwner;
		this._ownerNumber = Number(pOwnerNumber) || 0;
		this._containerID = pContainerID;
		this.reload();
	}

	// This plan sheet's addressable Code (its MCP handle), carried on the boot session record.
	_customerCode()
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
		return (tmpUser && tmpUser.CustomerCode) ? String(tmpUser.CustomerCode) : '';
	}

	// The owner's human number, from what showFor was handed, or resolved from the WorkItem
	// provider cache the detail page loaded (mount timing can leave the passed number at 0).
	_ownerNumberResolved()
	{
		if (this._ownerNumber) { return this._ownerNumber; }
		if (this._ownerType === 'WorkItem')
		{
			let tmpProvider = this.pict.providers.WorkItemAPI;
			let tmpRecord = (tmpProvider && typeof tmpProvider.store === 'function') ? tmpProvider.store().Index[this._idOwner] : null;
			if (tmpRecord && tmpRecord.WorkItemNumber) { this._ownerNumber = Number(tmpRecord.WorkItemNumber) || 0; }
		}
		return this._ownerNumber;
	}

	// The copy-paste line a person drops into a fresh Claude session to run one attached
	// prompt over the MCP. Empty unless this is a Work Item with a number and the plan sheet
	// has a Code (both are needed to address it).
	_runCommandFor(pEntry)
	{
		let tmpCode = this._customerCode();
		let tmpOwnerNumber = this._ownerNumberResolved();
		if (!tmpCode || this._ownerType !== 'WorkItem' || !tmpOwnerNumber) { return ''; }
		let tmpNum = (pEntry && pEntry.Prompt && pEntry.Prompt.PromptNumber) || 0;
		return 'Using the plansheet MCP, load the ' + tmpCode + ' plan sheet and run prompt ' + tmpNum + ' for Work Item ' + tmpOwnerNumber + '.';
	}

	reload()
	{
		if (!this._ownerType || !this._idOwner) { return Promise.resolve(); }
		return this._api().linksFor(this._ownerType, this._idOwner)
			.then((pLinks) =>
			{
				this._links = pLinks || [];
				this._paint();
				this._refreshOwnerBlueprint();
			})
			.catch(() => { this._links = []; this._paint(); });
	}

	// The Blueprint title block at the top of a Work Item's detail page shows its own "Prompts"
	// tile, loaded once by EntityDetail and otherwise only refreshed by pin/unpin. This panel's
	// reload only repaints itself (the lower connections block), so without this the top tile keeps
	// showing the stale count until the page is reloaded. Ping EntityDetail's blueprint reload when
	// it is mounted for the same Work Item.
	_refreshOwnerBlueprint()
	{
		if (this._ownerType !== 'WorkItem') { return; }
		let tmpDetail = this.pict.views['Plansheet-EntityDetail'];
		if (tmpDetail && tmpDetail._key === 'WorkItem' && tmpDetail._id === this._idOwner && typeof tmpDetail._reloadBlueprint === 'function')
		{
			tmpDetail._reloadBlueprint();
		}
	}

	_paint()
	{
		let tmpHost = this._containerID ? document.getElementById(this._containerID) : null;
		if (!tmpHost) { return; }
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
		let tmpCanEdit = this._canEdit();

		let tmpHTML = '';
		this._links.forEach((pEntry) =>
		{
			// One reusable PromptEmbed per attached prompt (mounted after innerHTML below): it renders
			// the title/badges, an inline expand of the prompt text, Copy, the MCP run command, and
			// in-place Edit -- replacing the old read-only "Read" modal.
			let tmpLink = pEntry.Link || {};
			tmpHTML += '<div class="ppa-embed-host" id="ppa-embed-' + tmpLink.IDPromptLink + '"></div>';
		});
		if (!tmpHTML)
		{
			tmpHTML = '<div class="ppa-empty">No prompts attached. Attach one to make this work prompt-driven: run it, capture the outcome, refine, re-run.</div>';
		}
		if (tmpCanEdit)
		{
			let tmpIsWorkItem = (this._ownerType === 'WorkItem');
			tmpHTML += '<div class="ppa-actions">'
				+ (tmpIsWorkItem ? '<button class="ppa-btn" onclick="' + tmpViewRef + '.generateDialog()">Generate prompt</button>' : '')
				+ '<button class="ppa-btn" onclick="' + tmpViewRef + '.attachDialog()">Attach a prompt</button>'
				+ '<button class="ppa-btn" onclick="' + tmpViewRef + '.pasteAndAttach()">Paste external prompt and attach</button>'
				+ '</div>';
			// If a work item has prompts but the plan sheet has no Code yet, the run block cannot be
			// addressed; nudge the owner to set one.
			if (tmpIsWorkItem && this._links.length && !this._customerCode())
			{
				tmpHTML += '<div class="ppa-empty">Set a plan sheet Code in Org Settings to enable the one-line MCP run command for these prompts.</div>';
			}
		}
		tmpHost.innerHTML = tmpHTML;

		// Mount the reusable embeds now that their hosts exist. Extra header actions (Record run /
		// Detach) route back to this view; a just-generated prompt mounts expanded (no modal).
		this._embeds = [];
		this._links.forEach((pEntry) =>
		{
			let tmpLink = pEntry.Link || {};
			let tmpEmbedHost = document.getElementById('ppa-embed-' + tmpLink.IDPromptLink);
			if (!tmpEmbedHost) { return; }
			let tmpActions = [{ Key: 'run', Label: 'Record run' }];
			if (tmpCanEdit) { tmpActions.push({ Key: 'detach', Label: 'Detach', Danger: true }); }
			let tmpEmbed = new libPromptEmbed(this.pict);
			tmpEmbed.configure(pEntry,
			{
				RunCommand: this._runCommandFor(pEntry),
				Editable: tmpCanEdit,
				Expanded: (this._expandIDPrompt > 0 && Number(pEntry.Prompt && pEntry.Prompt.IDPrompt) === Number(this._expandIDPrompt)),
				Actions: tmpActions,
				OnAction: (pKey) =>
				{
					if (pKey === 'run') { this.recordRun(tmpLink.IDPrompt); }
					else if (pKey === 'detach') { this.detach(tmpLink.IDPromptLink); }
				}
			});
			tmpEmbed.mount(tmpEmbedHost);
			this._embeds.push(tmpEmbed);
		});
		this._expandIDPrompt = 0;
	}

	viewVersion(pIDPromptLink)
	{
		let tmpEntry = this._links.find((pCandidate) => pCandidate.Link && Number(pCandidate.Link.IDPromptLink) === Number(pIDPromptLink));
		if (!tmpEntry || !tmpEntry.Version) { return; }
		let tmpPrompt = tmpEntry.Prompt || {};
		let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
		let tmpRunCommand = this._runCommandFor(tmpEntry);
		// The full prompt text, with one-click copies: the prompt itself, and (when addressable) the MCP
		// run command. This is where "show me the generated prompt" lands.
		let tmpContent =
			'<div style="display:flex; gap:8px; margin-bottom:8px; flex-wrap:wrap;">'
			+ '<button class="ppa-btn" onclick="' + tmpViewRef + '.copyPromptText(' + pIDPromptLink + ')">Copy prompt text</button>'
			+ (tmpRunCommand ? '<button class="ppa-btn" onclick="' + tmpViewRef + '.copyRunCommand(' + pIDPromptLink + ')">Copy MCP run command</button>' : '')
			+ '</div>'
			+ (tmpRunCommand ? '<div class="ppa-run" style="margin-bottom:10px;"><code class="ppa-run-text">' + _escapeHTML(tmpRunCommand) + '</code></div>' : '')
			+ '<pre style="max-height:52vh; overflow:auto; white-space:pre-wrap; font-size:12px;">' + _escapeHTML(tmpEntry.Version.Source || '') + '</pre>';
		this._modal().show(
		{
			title: (tmpPrompt.Title || 'Prompt') + ' (v' + tmpEntry.Version.Sequence + ' as attached)',
			content: tmpContent,
			buttons: [{ Hash: 'ok', Label: 'Close', Style: 'primary' }]
		});
	}

	// Copy the full generated prompt text (the pinned version Source) to the clipboard.
	copyPromptText(pIDPromptLink)
	{
		let tmpEntry = this._links.find((pCandidate) => pCandidate.Link && Number(pCandidate.Link.IDPromptLink) === Number(pIDPromptLink));
		let tmpText = (tmpEntry && tmpEntry.Version && tmpEntry.Version.Source) ? tmpEntry.Version.Source : '';
		if (!tmpText) { return; }
		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
		{
			navigator.clipboard.writeText(tmpText).then(() => this._toast('Prompt text copied.', 'success')).catch(() => this._toast('Copy failed.', 'error'));
		}
	}

	recordRun(pIDPrompt)
	{
		let tmpEntry = this._links.find((pCandidate) => pCandidate.Link && Number(pCandidate.Link.IDPrompt) === Number(pIDPrompt));
		let tmpTitle = (tmpEntry && tmpEntry.Prompt && tmpEntry.Prompt.Title) || 'prompt';
		let tmpIsTestRun = (this._ownerType === 'TestRun');
		this._modal().show(
		{
			title: 'Record a run of "' + tmpTitle + '"',
			content:
				'<p style="font-size:13px;">' + (tmpIsTestRun ? 'The run is tied to this test run.' : 'How did it go?') + '</p>'
				+ '<label style="font-size:12px;">Outcome</label>'
				+ '<select id="PPA-Run-Outcome" class="pict-input" style="width:100%; margin-bottom:8px;">'
				+ '<option value="">Not rated</option><option value="Success">Success</option>'
				+ '<option value="Mixed">Mixed</option><option value="Failure">Failure</option></select>'
				+ '<label style="font-size:12px;">Notes</label>'
				+ '<textarea id="PPA-Run-Notes" class="pict-input" style="width:100%; min-height:80px;"></textarea>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Record run', Style: 'primary' }
			]
		}).then((pChoice) =>
		{
			if (pChoice !== 'save') { return; }
			let tmpRun =
			{
				Outcome: (document.getElementById('PPA-Run-Outcome') || {}).value || '',
				Notes: (document.getElementById('PPA-Run-Notes') || {}).value || ''
			};
			if (tmpIsTestRun) { tmpRun.IDTestRun = this._idOwner; }
			this._api().recordRun(pIDPrompt, tmpRun)
				.then(() => this._toast('Run recorded; version pinned.', 'success'))
				.catch((pError) => this._toast(pError.message, 'error'));
		});
	}

	// NOT converted to the server-backed candidate search (WI #446 scope: convert the seven remaining
	// pickers from WI #434; this file was the seventh). mine()/shared() are not the store().List-page bug
	// the other six had -- neither endpoint pages at all, so every prompt in both libraries is already a
	// candidate here, not just page one. And CandidateSearch's generic mechanism does not fit: it asks
	// resolve() -> the generic Prompt CRUD route, which Prompt-Guards.js owner-scopes to the caller's own
	// rows (see that file's header) for exactly the privacy reason Prompts/Mine and Prompts/Shared exist
	// as separate custom routes -- a private prompt of someone else must never surface. Wiring this
	// picker onto CandidateSearch as-is would silently drop the Shared library from the results (only
	// Mine would come back), and building it a real fix (a searchable Mine+Shared server endpoint) is a
	// new capability, not a mechanical conversion -- out of this item's "do not redesign the search"
	// scope. Left as a precomputed Options list; flagged in WI #446 rather than half-converted.
	attachDialog()
	{
		Promise.all([this._api().mine(), this._api().shared()])
			.then((pResults) =>
			{
				let tmpMine = pResults[0] || [];
				let tmpShared = (pResults[1] || []).filter((pPrompt) =>
					!tmpMine.find((pOwn) => pOwn.IDPrompt === pPrompt.IDPrompt));
				if (!tmpMine.length && !tmpShared.length)
				{
					return this._toast('No prompts in the libraries yet. Craft one at Prompt Library first.', 'info');
				}
				let fPromptOpts = (pList, pTag) => pList.map((pPrompt) => ({ Value: String(pPrompt.IDPrompt), Text: pPrompt.Title + ' (#' + (pPrompt.PromptNumber || pPrompt.IDPrompt) + ')' + pTag }));
				let tmpPromptOptions = fPromptOpts(tmpMine, '  -  Mine').concat(fPromptOpts(tmpShared, '  -  Shared'));
				let tmpSelectedPrompt = 0;
				let tmpContent =
					// The picker's dropdown floats over the modal, anchored to its control. pict-section-picker
					// 1.8.2+ positions the fixed dropdown correctly inside a transform-centered modal, so no
					// per-modal .pps-pop override is needed (matches generateDialog).
					'<label style="font-size:12px;">Prompt</label>'
					+ '<div id="PPA-Attach-Prompt-host" style="margin-bottom:8px;"></div>'
					+ '<label style="font-size:12px;">Why this prompt here? (optional note)</label>'
					+ '<input id="PPA-Attach-Note" class="pict-input" style="width:100%;">';
				let tmpDialog = this._modal().show(
				{
					title: 'Attach a prompt (pins its current version)',
					content: tmpContent,
					buttons:
					[
						{ Hash: 'cancel', Label: 'Cancel' },
						{ Hash: 'save', Label: 'Attach', Style: 'primary' }
					]
				});
				let tmpAppP = this.pict.PictApplication;
				if (tmpAppP && typeof tmpAppP.mountPicker === 'function')
				{
					setTimeout(() => { tmpAppP.mountPicker('#PPA-Attach-Prompt-host', 'PPA-AttachPromptPicker', tmpPromptOptions, (pVal) => { tmpSelectedPrompt = Number(pVal) || 0; }, { Placeholder: 'Search prompts' }); }, 20);
				}
				return tmpDialog.then((pChoice) =>
				{
					if (pChoice !== 'save') { return; }
					let tmpIDPrompt = tmpSelectedPrompt || 0;
					let tmpNote = (document.getElementById('PPA-Attach-Note') || {}).value || '';
					if (!tmpIDPrompt) { return; }
					this._api().attach(tmpIDPrompt, this._ownerType, this._idOwner, tmpNote)
						.then(() => { this._toast('Attached.', 'success'); this.reload(); })
						.catch((pError) => this._toast(pError.message, 'error'));
				});
			})
			.catch((pError) => this._toast(pError.message, 'error'));
	}

	pasteAndAttach()
	{
		this._modal().show(
		{
			title: 'Paste an external prompt and attach it here',
			content:
				'<p style="font-size:13px;">Goes into your library as a freeform prompt with an external run recorded, then attaches here pinned at version 1.</p>'
				+ '<label style="font-size:12px;">Title</label>'
				+ '<input id="PPA-Paste-Title" class="pict-input" style="width:100%; margin-bottom:8px;">'
				+ '<label style="font-size:12px;">The prompt</label>'
				+ '<textarea id="PPA-Paste-Source" class="pict-input" style="width:100%; min-height:130px; font-family:monospace; font-size:12px;"></textarea>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Paste and attach', Style: 'primary' }
			]
		}).then((pChoice) =>
		{
			if (pChoice !== 'save') { return; }
			let tmpTitle = ((document.getElementById('PPA-Paste-Title') || {}).value || '').trim();
			let tmpSource = (document.getElementById('PPA-Paste-Source') || {}).value || '';
			if (!tmpTitle || !tmpSource.trim()) { return this._toast('A title and the prompt text are both needed.', 'error'); }
			this._api().paste({ Title: tmpTitle, Source: tmpSource })
				.then((pBody) => this._api().attach(pBody.Prompt.IDPrompt, this._ownerType, this._idOwner, ''))
				.then(() => { this._toast('Pasted and attached.', 'success'); this.reload(); })
				.catch((pError) => this._toast(pError.message, 'error'));
		});
	}

	detach(pIDPromptLink)
	{
		this._modal().confirm('The prompt and its history stay in the library; only the attachment goes (softly).',
			{ title: 'Detach this prompt?', confirmLabel: 'Detach' })
			.then((pOk) =>
			{
				if (!pOk) { return; }
				this._api().detach(pIDPromptLink)
					.then(() => this.reload())
					.catch((pError) => this._toast(pError.message, 'error'));
			});
	}

	// Generate a prompt for this work item from a shape (pre-filled + attached server-side).
	// Reachable from the panel's "Generate prompt" button and the detail header action.
	generateDialog()
	{
		if (this._ownerType !== 'WorkItem') { return this._toast('Prompts are generated for a work item.', 'info'); }
		let tmpShapeAPI = this.pict.providers.PromptShapeAPI;
		let tmpWorkItemAPI = this.pict.providers.WorkItemAPI;
		this._genExtraIDs = [];
		this._genCandidateIndex = {};
		Promise.all(
		[
			Promise.resolve(tmpShapeAPI ? tmpShapeAPI.available() : []),
			Promise.resolve(tmpWorkItemAPI && typeof tmpWorkItemAPI.list === 'function' ? tmpWorkItemAPI.list() : [])
		]).then((pResults) =>
		{
			let tmpShapes = Array.isArray(pResults[0]) ? pResults[0] : [];
			let tmpAllItems = Array.isArray(pResults[1]) ? pResults[1] : [];
			let tmpOptions = tmpShapes.map((pShape) =>
				'<option value="' + _escapeHTML(pShape.Key) + '"' + (pShape.Key === 'software-development' ? ' selected' : '') + '>' + _escapeHTML(pShape.Name || pShape.Key) + '</option>').join('');
			if (!tmpOptions) { tmpOptions = '<option value="software-development">Software Development</option>'; }
			// Candidate work items to bundle (this tenant's items except the one we're on). Kept as
			// full rows so the picker can filter them and show metadata (#236); also indexed so the
			// selected-bundle chips can label an id without another lookup.
			this._genCandidates = tmpAllItems.filter((pWI) => Number(pWI.IDWorkItem) !== Number(this._idOwner));
			this._genCandidates.forEach((pWI) => { this._genCandidateIndex[pWI.IDWorkItem] = { Number: pWI.WorkItemNumber || pWI.IDWorkItem, Title: pWI.Title || '' }; });
			// Land the picker on the same activity lens as the Work Items list: in-flight + recent,
			// newest first -- so the items a person most likely wants to bundle are on top, not the
			// oldest in database order (the #236 complaint about this exact dropdown).
			this._genFilter = Object.assign(libFocus.emptyState(), { activity: 'related' });
			let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
			this._modal().show(
			{
				title: 'Generate a prompt for this work item',
				width: 'min(940px, 95vw)',
				content:
					'<p style="font-size:13px;">Creates a prompt from a shape, pre-filled from this work item and attached here. Its guidelines (coding standards, screenshots, a documentation trail) come along automatically.</p>'
					+ '<label style="font-size:12px;">Shape</label>'
					+ '<select id="PPA-Gen-Shape" class="pict-input" style="width:100%;">' + tmpOptions + '</select>'
					+ '<div style="margin-top:14px;"><label style="font-size:12px;">Also include (optional)</label>'
					+ '<div style="font-size:11.5px;color:var(--theme-color-text-secondary,#889);margin:0 0 8px;">Bundle other work items into one prompt, done in the order added. The guidelines are carried once. Filter the list, then check the ones to add.</div>'
					+ '<input id="PPA-Gen-Search" class="pict-input ppa-pick-search" placeholder="Search work items..." oninput="' + tmpViewRef + '._genSetSearch(this.value)">'
					+ '<div id="PPA-Gen-Chips" class="ppa-pick-chiprow"></div>'
					+ '<div id="PPA-Gen-Table" class="ppa-pick-scroll"></div>'
					+ '<div id="PPA-Gen-Also-chips" class="ppa-pick-selected"></div></div>',
				buttons:
				[
					{ Hash: 'cancel', Label: 'Cancel' },
					{ Hash: 'save', Label: 'Generate', Style: 'primary' }
				]
			}).then((pChoice) =>
			{
				if (pChoice !== 'save') { return; }
				let tmpTypeKey = (document.getElementById('PPA-Gen-Shape') || {}).value || 'software-development';
				let tmpExtras = (this._genExtraIDs || []).slice();
				let tmpGen = tmpExtras.length
					? this._api().generateForWorkItems([this._idOwner].concat(tmpExtras), tmpTypeKey)
					: this._api().generateForWorkItem(this._idOwner, tmpTypeKey);
				tmpGen
					.then((pBody) =>
					{
						this._toast(tmpExtras.length ? ('Combined prompt generated for ' + (tmpExtras.length + 1) + ' work items.') : 'Prompt generated.', 'success');
						let tmpIDPrompt = pBody && pBody.Prompt && pBody.Prompt.IDPrompt;
						// Refresh the panel with the new prompt mounted expanded, so "Generate" ends with the
						// prompt on the page inline (no modal).
						this._expandIDPrompt = Number(tmpIDPrompt) || 0;
						return this.reload();
					})
					.catch((pError) => this._toast(pError.message, 'error'));
			});
			// Render the filter chips + the multi-select metadata table once the dialog DOM exists.
			setTimeout(() => { this._genRenderChipsBar(); this._genRenderTable(); this._genRenderChips(); }, 20);
		}).catch((pError) => this._toast(pError.message, 'error'));
	}

	// ── the "bundle other work items" picker (a filterable, multi-select list view, #236) ──

	// The candidate rows after the picker's live filter, newest-touched first (the activity intent):
	// what a person most likely wants to bundle floats to the top, instead of database order.
	_genVisibleRows()
	{
		let tmpFilter = this._genFilter || libFocus.emptyState();
		let tmpRows = libFocus.filterRows(this._genCandidates || [], tmpFilter, { CurrentUserID: 0, Agents: {} });
		return tmpRows.slice().sort((pA, pB) =>
		{
			let tmpA = Date.parse(pA.UpdateDate || pA.CreateDate || '') || 0;
			let tmpB = Date.parse(pB.UpdateDate || pB.CreateDate || '') || 0;
			if (tmpA !== tmpB) { return tmpB - tmpA; }
			return (Number(pB.WorkItemNumber || 0) - Number(pA.WorkItemNumber || 0));
		});
	}

	_genSetSearch(pValue)
	{
		this._genFilter = this._genFilter || libFocus.emptyState();
		this._genFilter.q = pValue || '';
		this._genRenderTable();
	}

	// Toggle a status lane in the picker filter. Turning a specific lane on means the person is
	// scanning that lane on purpose, so drop the activity window (which would otherwise still hide
	// older items in it); clearing the last lane restores the activity default.
	_genToggleStatus(pKey)
	{
		this._genFilter = this._genFilter || libFocus.emptyState();
		let tmpSet = {};
		String(this._genFilter.status || '').split(',').forEach((pS) => { if (pS.trim()) { tmpSet[pS.trim()] = true; } });
		if (tmpSet[pKey]) { delete tmpSet[pKey]; } else { tmpSet[pKey] = true; }
		this._genFilter.status = Object.keys(tmpSet).join(',');
		this._genFilter.activity = this._genFilter.status ? '' : 'related';
		this._genRenderChipsBar();
		this._genRenderTable();
	}

	_genToggleActivity()
	{
		this._genFilter = this._genFilter || libFocus.emptyState();
		this._genFilter.activity = (this._genFilter.activity === 'related') ? '' : 'related';
		this._genRenderChipsBar();
		this._genRenderTable();
	}

	// The filter chip row: a "Recent & active" toggle (the activity lens) plus one toggle per board
	// lane, so a person can widen from the default to any status.
	_genRenderChipsBar()
	{
		let tmpHost = (typeof document !== 'undefined') ? document.getElementById('PPA-Gen-Chips') : null;
		if (!tmpHost) { return; }
		let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
		let tmpFilter = this._genFilter || {};
		let tmpStatusSet = {};
		String(tmpFilter.status || '').split(',').forEach((pS) => { if (pS.trim()) { tmpStatusSet[pS.trim()] = true; } });
		let tmpHTML = '<button class="ppa-pick-chip' + (tmpFilter.activity === 'related' ? ' on' : '') + '" onclick="' + tmpViewRef + '._genToggleActivity()">Recent &amp; active</button>';
		(libDescriptors.BoardLanes || []).forEach((pKey) =>
		{
			tmpHTML += '<button class="ppa-pick-chip' + (tmpStatusSet[pKey] ? ' on' : '') + '" onclick="' + tmpViewRef + '._genToggleStatus(\'' + pKey + '\')">' + _escapeHTML(libDescriptors.BoardLaneLabels[pKey] || pKey) + '</button>';
		});
		tmpHost.innerHTML = tmpHTML;
	}

	// The multi-select metadata table: one row per visible candidate with a checkbox, number,
	// title, a Type glyph, status, and last-touched date -- so a person can peek at what each work
	// item is before adding it. Clicking anywhere on the row toggles its selection.
	_genRenderTable()
	{
		let tmpHost = (typeof document !== 'undefined') ? document.getElementById('PPA-Gen-Table') : null;
		if (!tmpHost) { return; }
		let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
		let tmpRows = this._genVisibleRows();
		let tmpSelected = {};
		(this._genExtraIDs || []).forEach((pID) => { tmpSelected[pID] = true; });
		let tmpIcon = this.pict.providers && this.pict.providers.Icon;
		let tmpBody = tmpRows.map((pWI) =>
		{
			let tmpID = Number(pWI.IDWorkItem);
			let tmpOn = !!tmpSelected[tmpID];
			let tmpTypeName = pWI.Type || '';
			let tmpGlyph = (tmpIcon && libDescriptors.WorkItemTypeIcons[tmpTypeName] && typeof tmpIcon.get === 'function') ? tmpIcon.get(libDescriptors.WorkItemTypeIcons[tmpTypeName]) : '';
			let tmpStatus = libDescriptors.BoardLaneLabels[pWI.Status] || pWI.Status || '';
			return '<tr class="ppa-pick-row' + (tmpOn ? ' on' : '') + '" onclick="' + tmpViewRef + '._genToggleRow(' + tmpID + ')">'
				+ '<td class="ppa-pick-cb"><input type="checkbox" ' + (tmpOn ? 'checked' : '') + ' onclick="event.stopPropagation();' + tmpViewRef + '._genToggleRow(' + tmpID + ')"></td>'
				+ '<td class="ppa-pick-num">#' + (pWI.WorkItemNumber || tmpID) + '</td>'
				+ '<td class="ppa-pick-title">' + _escapeHTML(pWI.Title || '') + '</td>'
				+ '<td class="ppa-pick-type" title="' + _escapeHTML(tmpTypeName) + '">' + (tmpGlyph || _escapeHTML(tmpTypeName)) + '</td>'
				+ '<td class="ppa-pick-status">' + _escapeHTML(tmpStatus) + '</td>'
				+ '<td class="ppa-pick-upd">' + _escapeHTML(_shortDate(pWI.UpdateDate)) + '</td>'
				+ '</tr>';
		}).join('');
		if (!tmpBody) { tmpBody = '<tr><td colspan="6" class="ppa-pick-empty">No work items match. Adjust the search or filters.</td></tr>'; }
		let tmpSelCount = (this._genExtraIDs || []).length;
		let tmpCount = tmpRows.length + ' shown' + (tmpSelCount ? (' &middot; ' + tmpSelCount + ' selected') : '');
		tmpHost.innerHTML = '<table class="ppa-pick-table"><thead><tr><th></th><th>#</th><th>Title</th><th>Type</th><th>Status</th><th>Updated</th></tr></thead>'
			+ '<tbody>' + tmpBody + '</tbody></table>'
			+ '<div class="ppa-pick-count">' + tmpCount + '</div>';
	}

	// Toggle one work item in the bundle (deduped; order preserved as check order). Repaints the
	// table (checkbox state) and the selected-chips row.
	_genToggleRow(pID)
	{
		pID = Number(pID) || 0;
		if (!pID || pID === Number(this._idOwner)) { return; }
		if (!Array.isArray(this._genExtraIDs)) { this._genExtraIDs = []; }
		let tmpIndex = this._genExtraIDs.indexOf(pID);
		if (tmpIndex >= 0) { this._genExtraIDs.splice(tmpIndex, 1); } else { this._genExtraIDs.push(pID); }
		this._genRenderTable();
		this._genRenderChips();
	}

	_genRemoveExtra(pID)
	{
		if (!Array.isArray(this._genExtraIDs)) { return; }
		let tmpIndex = this._genExtraIDs.indexOf(Number(pID));
		if (tmpIndex >= 0) { this._genExtraIDs.splice(tmpIndex, 1); this._genRenderChips(); this._genRenderTable(); }
	}

	_genRenderChips()
	{
		let tmpHost = (typeof document !== 'undefined') ? document.getElementById('PPA-Gen-Also-chips') : null;
		if (!tmpHost) { return; }
		let tmpViewRef = "_Pict.views['Plansheet-PromptAttachments']";
		tmpHost.innerHTML = (this._genExtraIDs || []).map((pID) =>
		{
			let tmpMeta = (this._genCandidateIndex || {})[pID] || { Number: pID, Title: '' };
			let tmpLabel = '#' + tmpMeta.Number + ' ' + tmpMeta.Title;
			return '<span style="display:inline-flex;align-items:center;gap:5px;max-width:100%;background:var(--theme-color-background-secondary,#eef1f6);border-radius:999px;padding:2px 4px 2px 10px;font-size:12px;">'
				+ '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + _escapeHTML(tmpLabel) + '</span>'
				+ '<button class="ppa-btn" style="border:none;background:none;cursor:pointer;padding:0 4px;font-size:14px;line-height:1;" onclick="' + tmpViewRef + '._genRemoveExtra(' + pID + ')">&times;</button></span>';
		}).join('');
	}

	// Copy the one-line MCP run command for one attached prompt to the clipboard.
	copyRunCommand(pIDPromptLink)
	{
		let tmpEntry = this._links.find((pCandidate) => pCandidate.Link && Number(pCandidate.Link.IDPromptLink) === Number(pIDPromptLink));
		let tmpText = tmpEntry ? this._runCommandFor(tmpEntry) : '';
		if (!tmpText) { return; }
		let fDone = () => this._toast('Run command copied.', 'success');
		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
		{
			navigator.clipboard.writeText(tmpText).then(fDone).catch(() => this._toast(tmpText, 'info'));
		}
		else { this._toast(tmpText, 'info'); }
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this._modal();
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}
}

module.exports = PictViewPromptAttachments;
module.exports.default_configuration = _ViewConfiguration;
